import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import checkoutBackground from '../../assets/images/checkoutBackground.svg';
import chevronDownIcon from '../../assets/images/chevron-down.svg';
import * as service from '../../utils/service';
import { AUTH_CATEGORIES, useAuthenticatedUser } from '../Auth';
import Spinner from '../Spinner/Spinner';
import { CheckoutForm, OrderSummary, SubscriptionSelector } from './components';
import { useCheckoutData, useStripePublishableKey } from './hooks';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const user = useAuthenticatedUser();
  const { userCategory, email } = user;

  const isProvider = userCategory === AUTH_CATEGORIES.PROVIDER;
  const isAdmin = userCategory === AUTH_CATEGORIES.ADMIN;

  const {
    data,
    isLoading: isLoadingData,
    error: dataError,
  } = useCheckoutData({ user, isAdmin });

  const { publishableKey: stripePublishableKey } = useStripePublishableKey();

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isSubscriptionDetailsOpen, setIsSubscriptionDetailsOpen] =
    useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Automatically open subscription details when a subscription is selected
  useEffect(() => {
    if (selectedSubscription) {
      setIsSubscriptionDetailsOpen(true);
    }
  }, [selectedSubscription]);

  const handleSubmit = (formData) => {
    if (!formData?.success) return;

    const baseurl = service.getHgproBaseurl(window.location.hostname);
    const planCode = isAdmin
      ? selectedSubscription?.planCode?.admin
      : selectedSubscription?.planCode?.provider;
    const isRosterSubscription =
      planCode === 'HGGRPA-ELEVATED' || planCode === 'HGGRPM-ELEVATED';

    const redirectUrl =
      isRosterSubscription || isAdmin
        ? `${baseurl}/elevatedRoster?fromSource=self-serve-success`
        : isProvider
          ? `${baseurl}/dashboard/index?fromSource=self-serve-success`
          : baseurl;

    // Show full-page spinner before redirect to prevent blank page
    // Use requestAnimationFrame to ensure spinner renders before redirect
    requestAnimationFrame(() => {
      setIsRedirecting(true);
      // Small delay to ensure spinner animation starts before redirect
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 150);
    });
  };

  const handleGoBack = () => {
    if (showPaymentForm) {
      setShowPaymentForm(false);
    } else {
      navigate('/add-providers-to-roster');
    }
  };

  const handleContinueToPayment = () => {
    setShowPaymentForm(true);
  };

  // Show spinner while loading cache data or redirecting
  if (isLoadingData || isRedirecting) {
    return (
      <div className="w-full flex items-stretch gap-1 shrink-0 bg-gray-10 min-h-screen lg:flex-row flex-col">
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-10">
          <Spinner cta={true} />
          <p className="mt-4 text-base text-gray-70 text-center">
            {isRedirecting
              ? 'Redirecting to your roster...'
              : 'Loading your selection...'}
          </p>
        </div>
      </div>
    );
  }

  // Show error state for admin users with cache issues
  // TODO: Replace with proper error component in follow-up PR
  if (isAdmin && dataError) {
    const errorMessages = {
      missing_session:
        'No session found. Please return to the roster builder to continue.',
      cache_not_found:
        'Your selection has expired or was not found. Please return to the roster builder to start over.',
      cache_error:
        'There was an error loading your selection. Please try again.',
    };

    return (
      <div className="w-full flex items-stretch gap-1 shrink-0 bg-gray-10 min-h-screen lg:flex-row flex-col">
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-10 px-5">
          <div className="max-w-md text-center flex flex-col items-center">
            <h1 className="text-2xl font-semibold text-gray-90 mb-4">
              Unable to Load Checkout
            </h1>
            <p className="text-base text-gray-70 mb-6">
              {errorMessages[dataError] || errorMessages.cache_error}
            </p>
            <button
              type="button"
              className="flex justify-center items-center gap-2.5 py-3 px-12 bg-deep-blue-30 text-white border-none rounded-full cursor-pointer text-sm font-semibold leading-5 transition-colors duration-300 hover:bg-deep-blue-50"
              onClick={() => navigate('/add-providers-to-roster')}
            >
              Return to Roster Builder
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex items-stretch gap-1 min-h-screen md:flex-row flex-col">
      {/* Left Side - Form Section */}
      <div className="bg-white w-full lg:w-[60%] flex flex-col items-start gap-10 lg:p-20 lg:pt-20 lg:pb-30 p-5 pt-5 pb-5 lg:gap-10 md:gap-7 shrink-0 lg:min-h-auto md:min-h-[648px]">
        {/* Back to Roster Button - Only show for roster/elevated roster flows */}
        {!isProvider && (
          <button
            type="button"
            className="flex items-center gap-1 bg-transparent border-none cursor-pointer transition-opacity duration-300 hover:opacity-80"
            onClick={handleGoBack}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label="Back arrow"
              className="w-3 h-3 m-2"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.56259 5.24997H11.2498C11.4488 5.24997 11.6395 5.32899 11.7802 5.46964C11.9208 5.61029 11.9998 5.80106 11.9998 5.99997C11.9998 6.19888 11.9208 6.38965 11.7802 6.5303C11.6395 6.67095 11.4488 6.74997 11.2498 6.74997H2.56259L6.00584 10.1932C6.14667 10.3339 6.22583 10.5249 6.2259 10.724C6.22597 10.923 6.14695 11.114 6.00622 11.2548C5.86548 11.3957 5.67457 11.4748 5.47548 11.4749C5.27639 11.475 5.08542 11.3959 4.94459 11.2552L0.21959 6.53022C0.0789866 6.38957 0 6.19884 0 5.99997C0 5.8011 0.0789866 5.61037 0.21959 5.46972L4.94459 0.744719C5.08552 0.603989 5.27658 0.525006 5.47575 0.525147C5.67491 0.525287 5.86586 0.60454 6.00659 0.745469C6.14732 0.886398 6.2263 1.07746 6.22616 1.27662C6.22602 1.47579 6.14677 1.66674 6.00584 1.80747L2.56259 5.24997Z"
                fill="#0202EA"
              />
            </svg>
            <span className="text-blue-50 text-base font-normal leading-6">
              {showPaymentForm
                ? 'Back to Select Your Subscription'
                : 'Back to Roster'}
            </span>
          </button>
        )}

        {/* Subscription Selector - Hidden on mobile after continuing to payment */}
        {!showPaymentForm && (
          <SubscriptionSelector
            selectedSubscription={selectedSubscription}
            onSubscriptionChange={setSelectedSubscription}
            providers={data?.selectedProviders || []}
          />
        )}

        {/* Mobile Order Summary Section - Collapsible - Show when subscription is selected and before payment form */}
        <div className="flex md:hidden flex-col items-start gap-5 self-stretch not-empty:py-7 not-empty:border-t not-empty:border-b not-empty:border-gray-20">
          {selectedSubscription && (
            <>
              {showPaymentForm && (
                <button
                  type="button"
                  className="flex justify-between items-center gap-10 self-stretch cursor-pointer lg:cursor-default lg:pointer-events-none"
                  onClick={() =>
                    setIsSubscriptionDetailsOpen(!isSubscriptionDetailsOpen)
                  }
                >
                  <div className="text-gray-90 text-xl font-semibold leading-8 lg:text-2xl lg:leading-9">
                    Subscription Details
                  </div>
                  <div
                    className={`w-5 h-5 transition-transform duration-300 shrink-0 lg:hidden ${isSubscriptionDetailsOpen ? 'rotate-180' : ''}`}
                  >
                    <img src={chevronDownIcon} alt="Chevron Icon" />
                  </div>
                </button>
              )}

              {isSubscriptionDetailsOpen && (
                <OrderSummary
                  selectedSubscription={selectedSubscription}
                  providers={data?.selectedProviders || []}
                  showTitle={showPaymentForm}
                  isMobile={true}
                  showPaymentForm={showPaymentForm}
                />
              )}
            </>
          )}
        </div>

        {/* Continue to Payment Button - Mobile only, always visible but disabled when no subscription */}
        {!showPaymentForm && (
          <button
            type="button"
            className="flex justify-center items-center gap-2.5 py-3 px-12 bg-deep-blue-30 text-white border-none rounded-full cursor-pointer text-sm font-semibold leading-5 self-stretch transition-colors duration-300 hover:bg-deep-blue-50 disabled:bg-gray-10 disabled:text-gray-50 disabled:cursor-not-allowed"
            onClick={handleContinueToPayment}
            disabled={!selectedSubscription}
          >
            Continue to payment
          </button>
        )}

        {/* Checkout Form - Always visible on desktop, shown after continue button on mobile */}
        {selectedSubscription && (
          <div
            className={`flex flex-col items-start gap-4 self-stretch lg:flex ${showPaymentForm ? '' : 'lg:hidden hidden'}`}
          >
            <CheckoutForm
              onSubmit={handleSubmit}
              selectedSubscription={selectedSubscription}
              stripePublishableKey={stripePublishableKey}
              cacheData={data}
              defaultEmail={email}
              isAdmin={isAdmin}
            />
          </div>
        )}
      </div>

      {/* Right Side - Order Summary (Desktop only) */}
      <div
        className="flex-1 hidden lg:flex items-start gap-5 py-20 pr-30 pl-20 bg-blue-50 self-stretch"
        style={{
          backgroundImage: `url(${checkoutBackground})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
        }}
      >
        <OrderSummary
          selectedSubscription={selectedSubscription}
          providers={data?.selectedProviders || []}
          showTitle={true}
          isMobile={false}
          showPaymentForm={showPaymentForm}
        />
      </div>
    </div>
  );
};

CheckoutPage.propTypes = {
  onSubmit: PropTypes.func,
  onBackToRoster: PropTypes.func,
};

export default CheckoutPage;
